import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';

import {
  estadoVisitaAgenda,
  ESTADO_AGENDA_COLOR,
  ESTADO_AGENDA_LABEL,
} from '../estadoVisitaAgenda';

dayjs.locale('es');

type Vista = 'mes' | 'semana' | 'dia';

interface CalendarioTecnicoProps {
  cursor: Dayjs;
  setCursor: (d: Dayjs) => void;
  visitas: any[];
  onSelect: (v: any) => void;
  isLoading?: boolean;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const clienteDe = (v: any) =>
  v?.prospecto_data?.razon_social ||
  v?.contrato_data?.cliente_data?.razon_social ||
  v?.numero_referencia ||
  'Cliente';

/** Pastilla compacta de una visita (mes/semana) — clicable. */
function VisitaPill({ v, onSelect }: { v: any; onSelect: (v: any) => void }) {
  const tag = estadoVisitaAgenda(v);
  const tecnico = v.flota_data?.nombre || 'Sin técnico';
  const hora = (v.hora_visita || '').slice(0, 5);
  return (
    <Tooltip title={`${ESTADO_AGENDA_LABEL[tag]} · ${hora} · ${tecnico} — ${clienteDe(v)}`} arrow>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => onSelect(v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(v);
          }
        }}
        sx={{
          bgcolor: ESTADO_AGENDA_COLOR[tag],
          color: '#fff',
          borderRadius: 1,
          px: 0.75,
          py: 0.4,
          fontSize: '0.72rem',
          fontWeight: 700,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          textShadow: '0 1px 1px rgba(0,0,0,0.28)',
          transition: 'filter .12s ease',
          '&:hover': { filter: 'brightness(1.1)' },
          '&:focus-visible': { outline: '2px solid #1d4ed8', outlineOffset: 1 },
        }}
      >
        {hora ? `${hora} ` : ''}
        {tecnico}
      </Box>
    </Tooltip>
  );
}

/** Fila detallada de una visita (día) — clicable. */
function VisitaRow({ v, onSelect }: { v: any; onSelect: (v: any) => void }) {
  const tag = estadoVisitaAgenda(v);
  const tecnico = v.flota_data?.nombre || 'Sin técnico';
  const hora = (v.hora_visita || '').slice(0, 5);
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onSelect(v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(v);
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1,
        borderRadius: 1.5,
        border: '1px solid #eef2f7',
        borderLeft: `5px solid ${ESTADO_AGENDA_COLOR[tag]}`,
        bgcolor: '#fff',
        cursor: 'pointer',
        transition: 'background-color .12s ease',
        '&:hover': { bgcolor: '#f8fafc' },
        '&:focus-visible': { outline: '2px solid #1d4ed8', outlineOffset: 1 },
      }}
    >
      <Typography sx={{ fontWeight: 800, minWidth: 52, color: '#0f172a' }}>{hora || '—'}</Typography>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {tecnico}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {clienteDe(v)}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: ESTADO_AGENDA_COLOR[tag], color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
        {ESTADO_AGENDA_LABEL[tag]}
      </Box>
    </Box>
  );
}

/**
 * Calendario propio (MUI + dayjs) con vistas Mes / Semana / Día. Reemplaza a
 * FullCalendar para garantizar clics fiables (onClick de React). Cada visita
 * abre el modal de Calidad de Visita + Ticket vía `onSelect`.
 */
const CalendarioTecnico: React.FC<CalendarioTecnicoProps> = ({
  cursor,
  setCursor,
  visitas,
  onSelect,
  isLoading,
}) => {
  const [vista, setVista] = useState<Vista>('mes');

  const byDate = useMemo(() => {
    const m: Record<string, any[]> = {};
    for (const v of visitas) {
      if (!v?.fecha_visita) continue;
      const key = dayjs(v.fecha_visita).format('YYYY-MM-DD');
      (m[key] ||= []).push(v);
    }
    Object.values(m).forEach((arr) =>
      arr.sort((a, b) => String(a.hora_visita || '').localeCompare(String(b.hora_visita || ''))),
    );
    return m;
  }, [visitas]);

  const unit: 'month' | 'week' | 'day' = vista === 'mes' ? 'month' : vista === 'semana' ? 'week' : 'day';
  const goPrev = () => setCursor(cursor.subtract(1, unit));
  const goNext = () => setCursor(cursor.add(1, unit));
  const goToday = () => setCursor(dayjs());

  // Lunes de la semana del cursor.
  const mondayOf = (d: Dayjs) => d.subtract((d.day() + 6) % 7, 'day');
  const todayKey = dayjs().format('YYYY-MM-DD');

  const titulo = (() => {
    if (vista === 'mes') return cursor.format('MMMM [de] YYYY');
    if (vista === 'semana') {
      const ws = mondayOf(cursor);
      return `${ws.format('DD MMM')} – ${ws.add(6, 'day').format('DD MMM YYYY')}`;
    }
    return cursor.format('dddd, DD [de] MMMM [de] YYYY');
  })();

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Toolbar: navegación + selector de vista */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={goPrev} aria-label="Anterior"><ChevronLeftIcon /></IconButton>
          <IconButton size="small" onClick={goNext} aria-label="Siguiente"><ChevronRightIcon /></IconButton>
          <Button size="small" variant="outlined" onClick={goToday} sx={{ ml: 0.5, textTransform: 'none' }}>Hoy</Button>
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize', color: '#0f172a', textAlign: 'center', flex: 1 }}>
          {titulo}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={vista}
          onChange={(_, val) => val && setVista(val)}
          aria-label="Vista del calendario"
        >
          <ToggleButton value="mes" sx={{ textTransform: 'none' }}>Mes</ToggleButton>
          <ToggleButton value="semana" sx={{ textTransform: 'none' }}>Semana</ToggleButton>
          <ToggleButton value="dia" sx={{ textTransform: 'none' }}>Día</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isLoading && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.6)', zIndex: 5, borderRadius: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {/* ---------- VISTA MES ---------- */}
      {vista === 'mes' && (() => {
        const monthStart = cursor.startOf('month');
        const gridStart = mondayOf(monthStart);
        const days = Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));
        return (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 0.5 }}>
              {WEEKDAYS.map((d) => (
                <Typography key={d} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: '#475569', py: 0.5 }}>{d}</Typography>
              ))}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
              {days.map((d) => {
                const key = d.format('YYYY-MM-DD');
                const inMonth = d.month() === cursor.month();
                const isToday = key === todayKey;
                const dayVisits = byDate[key] || [];
                return (
                  <Box key={key} sx={{
                    minHeight: 108, maxHeight: 168, border: '1px solid #eef2f7', borderRadius: 1.5, p: 0.5,
                    bgcolor: inMonth ? (isToday ? 'rgba(37,99,235,0.06)' : '#fff') : '#f8fafc',
                    opacity: inMonth ? 1 : 0.55, display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'hidden',
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: isToday ? 800 : 600, color: isToday ? '#2563eb' : '#334155', alignSelf: 'flex-end', lineHeight: 1 }}>
                      {d.date()}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, overflowY: 'auto' }}>
                      {dayVisits.map((v: any) => <VisitaPill key={v.id_agendamiento} v={v} onSelect={onSelect} />)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </>
        );
      })()}

      {/* ---------- VISTA SEMANA ---------- */}
      {vista === 'semana' && (() => {
        const ws = mondayOf(cursor);
        const weekDays = Array.from({ length: 7 }, (_, i) => ws.add(i, 'day'));
        return (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {weekDays.map((d) => {
              const key = d.format('YYYY-MM-DD');
              const isToday = key === todayKey;
              const dayVisits = byDate[key] || [];
              return (
                <Box key={key} sx={{ border: '1px solid #eef2f7', borderRadius: 1.5, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 340 }}>
                  <Box sx={{ textAlign: 'center', py: 0.75, bgcolor: isToday ? 'rgba(37,99,235,0.10)' : '#f1f5f9', borderBottom: '1px solid #eef2f7' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', display: 'block', textTransform: 'capitalize' }}>
                      {d.format('ddd')}
                    </Typography>
                    <Typography sx={{ fontWeight: isToday ? 800 : 700, color: isToday ? '#2563eb' : '#0f172a' }}>{d.date()}</Typography>
                  </Box>
                  <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', flex: 1 }}>
                    {dayVisits.length === 0 ? (
                      <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', mt: 1 }}>—</Typography>
                    ) : (
                      dayVisits.map((v: any) => <VisitaPill key={v.id_agendamiento} v={v} onSelect={onSelect} />)
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })()}

      {/* ---------- VISTA DÍA ---------- */}
      {vista === 'dia' && (() => {
        const key = cursor.format('YYYY-MM-DD');
        const dayVisits = byDate[key] || [];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 720, mx: 'auto' }}>
            {dayVisits.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
                <Typography>No hay visitas para este día.</Typography>
              </Box>
            ) : (
              dayVisits.map((v: any) => <VisitaRow key={v.id_agendamiento} v={v} onSelect={onSelect} />)
            )}
          </Box>
        );
      })()}
    </Box>
  );
};

export default CalendarioTecnico;
